/**
 * Partes do preview de anexo (SRP: preview ativo + carrossel).
 */
import React from 'react';
import { formatFileSize, isPdfFile } from '../../lib/fileMeta';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { ChatMediaThumb } from './ChatMediaThumb';
import { ChatDocChip } from './ChatDocChip';
import type { ChatMediaPreviewItem } from './ChatMediaPreviewModal';

export function ActivePreview({
    item,
    isMedia,
}: {
    item?: ChatMediaPreviewItem;
    isMedia: boolean;
}) {
    if (!item) {
        return <p className="text-body-sm text-muted">Nenhum arquivo selecionado.</p>;
    }

    if (isMedia) {
        if (item.file.type.startsWith('video/')) {
            return (
                <video
                    src={item.previewUrl}
                    controls
                    className="max-h-full max-w-full rounded-xl shadow-sm"
                />
            );
        }
        return (
            <img
                src={item.previewUrl}
                alt={item.file.name}
                className="max-h-full max-w-full rounded-xl object-contain shadow-sm"
            />
        );
    }

    if (isPdfFile(item.file)) {
        return (
            <iframe
                title={item.file.name}
                src={`${item.previewUrl}#toolbar=0&navpanes=0`}
                className="h-full min-h-[16rem] w-full max-w-3xl rounded-xl border border-subtle bg-card shadow-sm"
            />
        );
    }

    return (
        <div className="flex max-w-sm flex-col items-center gap-3 rounded-2xl border border-subtle bg-card px-8 py-10 shadow-sm">
            <span className="flex size-14 items-center justify-center rounded-xl bg-surface-subtle">
                <AppIcon name="file-text" className="size-7 text-destructive" aria-hidden />
            </span>
            <p className="max-w-full truncate text-center text-body-sm font-medium text-default">
                {item.file.name}
            </p>
            <p className="text-caption text-muted">{formatFileSize(item.file.size)}</p>
        </div>
    );
}

export function AttachStrip({
    items,
    isMedia,
    activeIndex,
    onSelect,
    onRemove,
    onAddMore,
}: {
    items: ChatMediaPreviewItem[];
    isMedia: boolean;
    activeIndex: number;
    onSelect: (index: number) => void;
    onRemove: (id: string) => void;
    onAddMore: () => void;
}) {
    return (
        <ul className="flex min-w-0 flex-1 list-none items-center gap-2 overflow-x-auto overscroll-contain p-0 scrollbar-premium">
            {items.map((item, index) => (
                <li key={item.id} className="shrink-0">
                    {isMedia ? (
                        <ChatMediaThumb
                            previewUrl={item.previewUrl}
                            file={item.file}
                            isActive={index === activeIndex}
                            onSelect={() => onSelect(index)}
                            onRemove={() => onRemove(item.id)}
                        />
                    ) : (
                        <ChatDocChip
                            file={item.file}
                            isActive={index === activeIndex}
                            onSelect={() => onSelect(index)}
                            onRemove={() => onRemove(item.id)}
                        />
                    )}
                </li>
            ))}
            <li className="shrink-0">
                <button
                    type="button"
                    onClick={onAddMore}
                    className={cn(
                        'flex size-14 items-center justify-center rounded-lg border border-dashed border-subtle',
                        'bg-card text-muted transition-colors sm:size-16',
                        'hover:border-primary hover:text-primary',
                        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20'
                    )}
                    aria-label="Adicionar mais arquivos"
                >
                    <AppIcon name="plus" className="size-5" aria-hidden />
                </button>
            </li>
        </ul>
    );
}
